/**
 * The "Send update" pop-up: find a client, choose which of their open cases it covers, draft one
 * update, and send it by email, text and portal. It is recorded once on each chosen case.
 */

export interface ClientUpdateRecipient {
  User_guid: string;
  name: string;
  email: string | null;
  phone: string | null;
}

export interface ClientUpdateCase {
  ticketGuid: string;
  citationNumber: string | null;
  courtName: string | null;
  statusName: string | null;
}

export interface ClientUpdateCases {
  cases: ClientUpdateCase[];
  recipientEmail: string | null;
  phone: string | null;
}

export interface ClientUpdateDraft {
  subject: string;
  body: string;
  recipientEmail: string | null;
  /** False when the AI was unavailable and the short portal note was offered instead. */
  aiDrafted: boolean;
}

export interface ClientUpdateDelivery {
  email: boolean;
  text: { sent: boolean; refused?: 'no-phone' | 'no-consent' | string };
  portal: true;
  citations: string[];
  recipientEmail: string | null;
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data?.message || fallback;
  } catch {
    return fallback;
  }
}

class ClientUpdateApi {
  private static base(): string {
    const host = process.env.API_HOST?.trim() || window.location.origin;
    return `${host}/api/v1/manager/client-update`;
  }

  /** Clients matching a name, email or phone number. */
  static async search(term: string): Promise<ClientUpdateRecipient[]> {
    const query = new URLSearchParams({ q: term });
    const response = await fetch(`${ClientUpdateApi.base()}/search?${query.toString()}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to search clients'));
    }
    return response.json();
  }

  /** The client's open cases, which an update can cover. */
  static async cases(userGuid: string): Promise<ClientUpdateCases> {
    const response = await fetch(`${ClientUpdateApi.base()}/${userGuid}/cases`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to load the client’s cases'));
    }
    return response.json();
  }

  /** A draft covering the chosen cases, for staff to read and edit. Nothing is sent. */
  static async draft(userGuid: string, ticketGuids: string[]): Promise<ClientUpdateDraft> {
    const response = await fetch(`${ClientUpdateApi.base()}/${userGuid}/draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ticketGuids }),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to draft the update'));
    }
    return response.json();
  }

  static async send(
    userGuid: string,
    subject: string,
    body: string,
    ticketGuids: string[],
  ): Promise<ClientUpdateDelivery> {
    const response = await fetch(`${ClientUpdateApi.base()}/${userGuid}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ subject, body, ticketGuids }),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Failed to send the update'));
    }
    return response.json();
  }
}

export default ClientUpdateApi;
